const ALLOWED_DECISIONS = new Set([
  'ACTIVE',
  'RETIRED',
  'ABSENT_RETIRED',
  'IN_PROGRESS',
  'UNKNOWN',
  'NOT_OBSERVED',
  'EXPIRED_UNOBSERVED',
  'COMMAND_CONFLICT',
]);

export function normalizeSportsnewPlacementAttemptResolution(response) {
  const decision = response?.data?.decision;
  return typeof decision === 'string' && ALLOWED_DECISIONS.has(decision)
    ? decision
    : 'UNKNOWN';
}

export function applySportsnewPlacementAttemptResolution({
  runtime,
  attempt,
  decision,
  userId,
} = {}) {
  if (!runtime || !attempt || Number(attempt.userId) !== Number(userId)) {
    return Object.freeze({ retired: false, refreshWallet: false });
  }
  if (['ACTIVE', 'RETIRED'].includes(decision)) {
    return Object.freeze({
      retired: runtime.retire(attempt.idempotencyKey, userId, attempt.challenge) === true,
      refreshWallet: true,
    });
  }
  if (['ABSENT_RETIRED', 'EXPIRED_UNOBSERVED'].includes(decision)) {
    return Object.freeze({
      retired: runtime.retire(attempt.idempotencyKey, userId, attempt.challenge) === true,
      refreshWallet: false,
    });
  }
  runtime.markUnknown(attempt.idempotencyKey, userId, attempt.challenge);
  return Object.freeze({ retired: false, refreshWallet: false });
}

export async function reconcileSportsnewPlacementAttempts({
  attempts = [],
  runtime,
  userId,
  resolveAttempt,
  captureGuard,
  isGuardCurrent,
  refreshWallet,
  isCancelled = () => false,
} = {}) {
  const summary = {
    attempted: 0,
    resolved: 0,
    failed: 0,
    walletRefreshes: 0,
    stopped: false,
  };
  const finish = () => Object.freeze({ ...summary });

  for (const attempt of attempts) {
    if (isCancelled()) {
      summary.stopped = true;
      return finish();
    }

    let guard = null;
    let result;
    summary.attempted += 1;
    try {
      guard = captureGuard(attempt);
      const response = await runtime.runSingleFlight({
        idempotencyKey: attempt.idempotencyKey,
        phase: 'RESOLUTION',
      }, () => resolveAttempt(attempt, guard));
      const decision = normalizeSportsnewPlacementAttemptResolution(response);
      result = applySportsnewPlacementAttemptResolution({
        runtime,
        attempt,
        decision,
        userId,
      });
      if (isCancelled() || !isGuardCurrent(guard, attempt)) {
        summary.stopped = true;
        return finish();
      }
      if (result.refreshWallet) {
        await refreshWallet(guard);
        summary.walletRefreshes += 1;
      }
    } catch {
      try {
        runtime.markUnknown(attempt.idempotencyKey, userId, attempt.challenge);
      } catch {
        // Persistence remains fail-closed; another independent slot may still reconcile.
      }
      summary.failed += 1;
      if (isCancelled() || (guard && !isGuardCurrent(guard, attempt))) {
        summary.stopped = true;
        return finish();
      }
      continue;
    }

    summary.resolved += 1;
  }

  return finish();
}
